import React, {useEffect, useRef, useState} from "react";
import {GridActionsCellItem} from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from '@mui/icons-material/Edit';
import useCallDataApi from "../../hooks/data";
import {useAuth} from "../../context/AuthContext";
import {useSnackbar} from "notistack";
import TrackingDialog from "../../components/TrackingDialog";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CloseIcon from "@mui/icons-material/Close";
import PersistentDrawerLeft from "../../components/NewDrawer";
import Loading from "../../components/Loading";
import TourOfficeDetail from "./TourOfficeDetail";
import DinoDialog from "../../components/Dialog";
import {Button, Stack} from "@mui/material";
import NewTourOfficeData from "./NewTourOfficeData";
import DataGridWithSearch from "../../components/DataGridWithSearch";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import useDataModel from "../../hooks/dataModel";
import {combineLoading} from "../../utils/utils";


const TourOffices = () => {
    const [isDeleteTourOfficeDialogOpen, toggleDeleteTourOfficeDialog] = useState(false)
    const [isEditTourOfficeDialogOpen, toggleEditTourOfficeDialog] = useState(false)
    const [isDetailDialogOpen, toggleDetailDialog] = useState(false)
    const [isTourOfficeDialogOpen, toggleTourOfficeDialog] = useState(false)
    const [isTrackingDialogOpen, toggleTrackingDialog] = useState(false)
    const [isValidateDialogOpen, toggleValidateDialog] = useState(false)
    const [current, setCurrent] = useState(1)
    const [tracking, setTracking] = useState([])
    const editTourOfficeForm = useRef(null)
    const newTourOfficeForm = useRef(null)

    const {
        models: tourOffices,
        setModels: setTourOffices,
        findModel: findTourOffice,
        fetchModels: fetchTourOffices,
        fetchModel,
        createModel: newTourOffice,
        updateModel: editTourOffice,
        deleteModel: deleteTourOffice,
        loading: tourOfficeLoading,
    } = useDataModel(useCallDataApi("tour-offices"))

    const {fetchModels: fetchContactPersons, loading: contactLoading} = useDataModel(useCallDataApi("contact-person"))
    const {fetchModels: fetchUsers, findModel: findUser, loading: usersLoading} = useDataModel(useCallDataApi("users"))

    const {getUser} = useAuth()
    const user = getUser()
    const permissions = user?.permissions
    const {enqueueSnackbar} = useSnackbar();

    const getTracking = id => fetchModel(`${id}/track`, setTracking)

    useEffect(() => {
        fetchTourOffices()
        fetchContactPersons()
        fetchUsers()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const invalidate = id => fetchModel(`${id}/invalidate`, model => {
        const {id} = model
        const editedTourOfficeList = tourOffices.filter(t => (id !== t.id))
        setTourOffices(editedTourOfficeList)
        enqueueSnackbar("Érvénytelenítve!", {variant: 'success'})
    })

    const columns = [
        {
            field: "manage",
            headerName: "Kezelés",
            sortable: false,
            type: "actions",
            getActions: (params) => {
                let menu = []
                if (permissions.includes('delete_touroffice')) menu.push(<GridActionsCellItem
                    icon={<DeleteIcon/>}
                    onClick={() => {
                        setCurrent(params.row?.id)
                        toggleDeleteTourOfficeDialog(true)
                    }}
                    label="Törlés"
                    showInMenu={true}
                />)
                if (permissions.includes('change_touroffice')) menu.push(<GridActionsCellItem
                    icon={<EditIcon/>}
                    label="Szerkesztés"
                    onClick={() => {
                        setCurrent(params.row?.id)
                        toggleEditTourOfficeDialog(true)
                    }}
                    showInMenu={true}/>)

                if (permissions.includes('can_track_touroffice')) menu.push(<GridActionsCellItem
                    icon={<AccountTreeIcon/>}
                    onClick={() => {
                        setCurrent(params.row?.id)
                        getTracking(params.row?.id)
                        toggleTrackingDialog(true)
                    }}
                    label="Nyomonkövetés"
                    showInMenu={true}
                />)
                if (permissions.includes('can_validate_touroffice')) menu.push(<GridActionsCellItem
                    icon={<CloseIcon/>}
                    onClick={() => {
                        setCurrent(params.row?.id)
                        toggleValidateDialog(true)
                    }}
                    label="Érvénytelenítés"
                    showInMenu={true}
                />)
                return menu
            },
        },
        {field: "name", headerName: "Név", width: 130},
        {field: "short_name", headerName: "Rövid név", width: 100},
        {field: "address", headerName: "Cím", width: 150},
        {field: "vat_number", headerName: "Adószám", width: 110},
        {field: "is_company", type: "boolean", headerName: "Egyéb cég?", width: 100},
        {field: "user", headerName: "Hozzáadta", width: 150, renderCell: p => findUser(p?.row?.user)?.email},
        {field: "billingo_partner_id", headerName: "Billingo partner azonosító", width: 150},
    ];

    const onRowClick = (params) => {
        setCurrent(params['id'])
        toggleDetailDialog(true)
    }

    const newDialogActions = <Stack direction='row' spacing={2} marginRight={2} marginBottom={2}>
        <Button size='large' variant='outlined' onClick={() => toggleTourOfficeDialog(false)}>Mégsem</Button>
        <Button size='large' variant='outlined' color='success' onClick={() => {
            const data = newTourOfficeForm.current.getData()
            if (Object.values(data).length > 0) {
                toggleTourOfficeDialog(false)
                newTourOffice(data)
            }
        }}>Hozzáadás</Button>
    </Stack>

    const editDialogActions = <Stack direction='row' spacing={2} marginRight={2} marginBottom={2}>
        <Button size='large' variant='outlined' onClick={() => toggleEditTourOfficeDialog(false)}>Mégsem</Button>
        <Button size='large' variant='outlined' color='success' onClick={() => {
            const data = editTourOfficeForm.current.getData();
            if (Object.values(data).length > 0) {
                toggleEditTourOfficeDialog(false)
                editTourOffice(current, data)
            }
        }}>Szerkesztés</Button>
    </Stack>

    const invalidateDialogActions = <Stack direction='row' spacing={2} marginRight={2} marginBottom={2}>
        <Button size='large' variant='outlined' onClick={() => toggleValidateDialog(false)}>Mégsem</Button>
        <Button size='large' variant='outlined' color='error' onClick={() => {
            toggleValidateDialog(false)
            invalidate(current)
        }}>érvénytelenítés</Button>
    </Stack>

    const deleteDialogActions = <Stack direction='row' spacing={2} marginRight={2} marginBottom={2}>
        <Button size='large' variant='outlined' onClick={() => toggleDeleteTourOfficeDialog(false)}>Mégsem</Button>
        <Button size='large' variant='contained' color='error' onClick={() => {
            toggleDeleteTourOfficeDialog(false)
            deleteTourOffice(current)
        }}>törlés</Button>
    </Stack>

    return <PersistentDrawerLeft title="Megrendelők" buttons={[
        <Button key='b1' color='success'
                disabled={!permissions?.includes('add_touroffice')}
                variant='outlined'
                endIcon={<AddCircleOutlineRoundedIcon/>}
                onClick={() => toggleTourOfficeDialog(true)}>Új
            megrendelő</Button>]}
    >
        <DataGridWithSearch name='tour-offices' onRowClick={onRowClick} rows={tourOffices} columns={columns}/>

        <DinoDialog
            handleClose={() => toggleDeleteTourOfficeDialog(false)}
            actions={deleteDialogActions}
            open={isDeleteTourOfficeDialogOpen}
            title={`Biztosan ki akarja törölni: ${findTourOffice(current)?.name}?`}
            subtitle='Minden kapcsolódó adat elvész.'/>

        <DinoDialog
            title="Megrendelő szerekesztése"
            open={isEditTourOfficeDialogOpen}
            handleClose={() => toggleEditTourOfficeDialog(false)}
            actions={editDialogActions}>
            <NewTourOfficeData reference={editTourOfficeForm} obj={findTourOffice(current)}/>
        </DinoDialog>

        <TrackingDialog
            model={findTourOffice(current)?.name}
            open={isTrackingDialogOpen}
            onClose={() => (toggleTrackingDialog(false))}
            trackingInfo={tracking}/>

        <DinoDialog
            title="Új megrendelő"
            open={isTourOfficeDialogOpen}
            handleClose={() => toggleTourOfficeDialog(false)}
            actions={newDialogActions}>
            <NewTourOfficeData reference={newTourOfficeForm}/>
        </DinoDialog>

        <DinoDialog
            handleClose={() => toggleValidateDialog(false)}
            actions={invalidateDialogActions}
            open={isValidateDialogOpen}
            title={`Biztosan érvényteleníti: ${findTourOffice(current)?.name}?`}
            subtitle='Csak admin felhasználó tudja ezt visszavonni.'/>

        <DinoDialog
            handleClose={() => toggleDetailDialog(false)}
            open={isDetailDialogOpen}
            title={findTourOffice(current)?.name}
            subtitle={findTourOffice(current)?.short_name}
        >
            <TourOfficeDetail tourOfficeId={current}/>
        </DinoDialog>

        <Loading isLoading={combineLoading([tourOfficeLoading, usersLoading, contactLoading])}/>
    </PersistentDrawerLeft>
};

export default TourOffices;
